import { useEffect, useState } from 'react';

import { DeleteOutlined as DeleteOutlinedIcon, Edit as EditIcon } from '@mui/icons-material';
import { HttpStatusCode } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import { ConfirmationDialog, FormDialog, ResourceNotFoundFallback } from '~components';
import { useSnackbar } from '~hooks';
import { DetailsGridLayout, usePageLayoutProperties } from '~layouts';

import ScheduleGeneralInfoCard from '../components/cards/scheduleGeneralInfoCard';
import AccessSchedulesForm from '../forms/accessSchedulesForm/accessSchedulesForm';
import { SkcScheduleNew } from '../interfaces/skcSchedule';
import SkcSchedulesService from '../services/skcSchedulesService';

interface UserAction {
	type: 'delete' | 'edit';
	label?: string;
	id: string;
}

const service = new SkcSchedulesService();

interface ScheduleDetailsContainerProps {
	id: string;
}

const ScheduleDetailsContainer = ({ id }: ScheduleDetailsContainerProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { enqueueSuccessSnackbar, enqueueAxiosErrorSnackbar } = useSnackbar();
	const { setPageTitle, setPageHeaderButtons: setPageButtons } = usePageLayoutProperties();

	const [userAction, setUserAction] = useState<UserAction | null>(null);

	const { data, error } = useSWR(
		[service.basePath, id],
		([_, args]) => service.getScheduleById(args),
		{
			onSuccess: (res, k, l) => console.log('success'),
		}
	);

	const { trigger: triggerEdit, isMutating: isEditMutating } = useSWRMutation(
		[service.basePath, id],
		([_, id], { arg }: { arg: SkcScheduleNew }) => service.updateSchedule(id, arg),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(`${t('ui.success.message.updated')} ${t('schedule').toLowerCase()}`);
				setUserAction(null);
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	const { trigger: triggerDelete, isMutating: isDeleteMutating } = useSWRMutation(
		[service.basePath, id],
		([_, id]) => service.deleteSchedule(id),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(`${t('ui.successfullyDeleted')} ${t('schedule').toLowerCase()}`);
				navigate('..', { relative: 'path' });
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	useEffect(() => {
		setPageButtons([
			{
				children: t('ui.edit'),
				startIcon: <EditIcon />,
				onClick: () => setUserAction({ type: 'edit', id: id }),
			},
			{
				children: t('ui.delete'),
				color: 'error',
				startIcon: <DeleteOutlinedIcon />,
				onClick: () => setUserAction({ type: 'delete', id: id }),
			},
		]);
	}, [id, setPageButtons, t]);

	useEffect(() => {
		if (data) {
			setPageTitle(data.label);
		}
	}, [setPageTitle, data]);

	if (error?.status === HttpStatusCode.NotFound) {
		return <ResourceNotFoundFallback label={id} type={t('schedule')} />;
	}

	return (
		<>
			<DetailsGridLayout>
				<ScheduleGeneralInfoCard id={id} />
			</DetailsGridLayout>
			<FormDialog
				open={userAction?.type === 'edit'}
				loading={isEditMutating}
				maxWidth='md'
				fullWidth
				onClose={() => setUserAction(null)}
			>
				<AccessSchedulesForm id={id} onSubmit={async (value) => await triggerEdit(value)} />
			</FormDialog>
			<ConfirmationDialog
				subTitle={t('thisWillDeleteResourceWithName', {
					resource: t('schedule'),
					name: userAction?.label ?? userAction?.id,
				})}
				open={userAction?.type === 'delete'}
				loading={isDeleteMutating}
				onClose={() => setUserAction(null)}
				onConfirm={async () => await triggerDelete()}
			/>
		</>
	);
};

export default ScheduleDetailsContainer;
